import React, { useRef, useState, useEffect } from "react";
import "../assets/styles/MainSection.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLinkedin,
  faGithub,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons";
import { faFilePdf } from "@fortawesome/free-solid-svg-icons";
import Xarrow from "react-xarrows";
import { TypeAnimation } from "react-type-animation";
import GraphemeSplitter from "grapheme-splitter";
import { useTheme } from "../context/ThemeContext";
import { motion } from "framer-motion";
import PageTransition from "../components/PageTransition";

const splitter = new GraphemeSplitter();

// Animation variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
  },
};

function MainSection() {
  const { theme } = useTheme();

  const openLink = (url) => {
    window.open(url, "_blank", "noopener noreferrer");
  };

  const box1Ref = useRef(null);
  const box2Ref = useRef(null);

  const [arrowSize, setArrowSize] = useState({ strokeWidth: 2 });
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    // Function to update arrow size based on screen width
    const updateSizes = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      const newStrokeWidth = width < 768 ? 1 : width < 1024 ? 2 : 3;
      setArrowSize({ strokeWidth: newStrokeWidth });
    };

    updateSizes();
    window.addEventListener("resize", updateSizes);

    return () => window.removeEventListener("resize", updateSizes);
  }, []);

  return (
    <PageTransition>
      <section
        className="main-section"
        role="main"
        aria-labelledby="main-heading"
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="main-content-wrapper"
        >
          <motion.div
            variants={itemVariants}
            className="greeting-container"
            aria-live="polite"
            aria-atomic="true"
          >
            <div className="greeting-text">
              <TypeAnimation
                splitter={(str) => splitter.splitGraphemes(str)}
                cursor={false}
                className="type"
                sequence={[
                  "Hello 🇬🇧",
                  3500,
                  "Ciao 🇮🇹",
                  3500,
                  "你好 🇨🇳",
                  3500,
                  "дравейте 🇧🇬",
                  3500,
                  "Hola 🇪🇸",
                  3500,
                  "Bonjour 🇫🇷",
                  3500,
                  "नमस्ते 🇮🇳",
                  2000,
                  "Olá 🇵🇹",
                  3500,
                  "Hallo 🇩🇪",
                  3500,
                  "こんにちは 🇯🇵",
                  3500,
                  "안녕하세요 🇰🇷",
                  3500,
                ]}
                style={{
                  fontSize: windowWidth < 480 ? "1.5em" : "2em",
                  fontWeight: "600",
                  color: "var(--text-color)",
                  textAlign: "center",
                }}
                repeat={Infinity}
              />
            </div>
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="profile-image-container"
          >
            <img
              src={`${process.env.PUBLIC_URL}/images/leo.png`}
              alt="Leonardo Scappatura portrait"
              className="profile-image"
            />
          </motion.div>

          <motion.div
            variants={itemVariants}
            className="text name-text"
            aria-live="polite"
            aria-atomic="true"
          >
            <TypeAnimation
              splitter={(str) => splitter.splitGraphemes(str)}
              sequence={[
                "My name is Leonqrdp Sc",
                1000,
                "My name is Leonardo scapp",
                1000,
                "My name is Leonardo Scappatura 🔥",
                1000,
              ]}
              cursor={false}
              className="type"
              speed={45}
              style={{
                fontSize: windowWidth < 480 ? "1.2em" : "1.5em",
                fontWeight: "600",
                color: "var(--text-color)",
                textAlign: "center",
              }}
            />
          </motion.div>

          <motion.div
            variants={itemVariants}
            style={{
              width: "90%",
              maxWidth: "700px",
              height: "auto",
              textAlign: "center",
              padding: "0.5em",
              fontSize: windowWidth < 480 ? "0.85rem" : "0.95rem",
            }}
            id="main-heading"
          >
            <p>
              Master's student in High Performance Computing at Politecnico di
              Milano and an alumnus of the Alta Scuola Politecnica honours
              program. Graduated with honors in Computer Science & AI at La
              Sapienza University of Rome.
              <br />
              <br />
              Previously explored{" "}
              <strong>
                deep learning for encrypted network traffic analysis
              </strong>
              . Currently focused on{" "}
              <strong>energy consumption forecasting</strong> and
              <strong>
                {" "}
                neural decoding for visual perception reconstruction
              </strong>
              .
              <br />
              <br />
              Research interests:{" "}
              <strong>deep learning, neuroscience, quantum computing</strong>.
            </p>
          </motion.div>

          <motion.nav
            variants={itemVariants}
            className="social-links"
            aria-label="Social media links"
          >
            <div
              role="button"
              tabIndex="0"
              aria-label="LinkedIn profile"
              title="Connect on LinkedIn"
              className="social-icon-wrapper linkedin"
              onClick={() =>
                openLink("https://www.linkedin.com/in/leonardosca")
              }
            >
              <FontAwesomeIcon
                icon={faLinkedin}
                className="social-icon"
                aria-hidden="true"
              />
              <span className="icon-label">LinkedIn</span>
            </div>
            <div
              role="button"
              tabIndex="0"
              aria-label="GitHub profile"
              title="View my projects on GitHub"
              className="social-icon-wrapper github"
              onClick={() => openLink("https://github.com/leonard013")}
            >
              <FontAwesomeIcon
                icon={faGithub}
                className="social-icon"
                aria-hidden="true"
              />
              <span className="icon-label">GitHub</span>
            </div>
            <div
              role="button"
              tabIndex="0"
              aria-label="Instagram profile"
              title="Follow on Instagram"
              className="social-icon-wrapper instagram"
              onClick={() =>
                openLink("https://www.instagram.com/leonardoscappatura/")
              }
            >
              <FontAwesomeIcon
                icon={faInstagram}
                className="social-icon"
                aria-hidden="true"
              />
              <span className="icon-label">Instagram</span>
            </div>
            <div
              role="button"
              tabIndex="0"
              aria-label="View Resume"
              title="View my Resume"
              className="social-icon-wrapper resume"
              onClick={() =>
                openLink(
                  `${process.env.PUBLIC_URL}/documents/Leonardo_Scappatura_Resume.pdf`
                )
              }
            >
              <FontAwesomeIcon
                icon={faFilePdf}
                className="social-icon"
                aria-hidden="true"
              />
              <span className="icon-label">Resume</span>
            </div>
          </motion.nav>
        </motion.div>
      </section>
    </PageTransition>
  );
}

export default MainSection;
